import React, { useState, useEffect, useCallback } from 'react';
import texasApi from '../../services/texasApi';

export interface TemplateEntry {
  slug: string;
  version: number | string;
  jurisdiction?: string;
  locale?: string;
  renderer?: string;
  title?: string;
  fieldsSchema?: unknown;
}

export interface RenderResult {
  base64: string;
  pageCount: number;
  warnings: string[];
  issues: string[];
  template: { slug: string; version: number | string; jurisdiction?: string; locale?: string; renderer?: string };
}

export interface FormSchemaEntry {
  entityType: string;
  version: number | string;
  jurisdictions?: string[];
  fields?: Array<{ name?: string; label?: string; type?: string }>;
}

function unwrap<T>(res: any): T | null {
  const body = res?.data;
  if (!body || typeof body !== 'object') return null;
  if ('success' in body) return body.success ? ((body.data ?? null) as T) : null;
  return ((body.data ?? body) as T) ?? null;
}

function apiError(res: any): string | null {
  const body = res?.data;
  if (body && typeof body === 'object' && body.success === false) {
    return body.error ?? body.message ?? 'Request failed';
  }
  return null;
}

function unique(values: Array<string | undefined>): string[] {
  return Array.from(new Set(values.filter((v): v is string => Boolean(v)))).sort();
}

export const TexasTemplateLibrary: React.FC = () => {
  const [templates, setTemplates] = useState<TemplateEntry[]>([]);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [jurisdiction, setJurisdiction] = useState('TX');
  const [locale, setLocale] = useState('');
  const [renderer, setRenderer] = useState('');

  const [resolveSlug, setResolveSlug] = useState('');
  const [resolveVersion, setResolveVersion] = useState('');
  const [resolveJurisdiction, setResolveJurisdiction] = useState('TX');
  const [resolveLocale, setResolveLocale] = useState('');
  const [resolved, setResolved] = useState<TemplateEntry | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);

  const [renderData, setRenderData] = useState('{}');
  const [renderResult, setRenderResult] = useState<RenderResult | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);

  const [formSchemas, setFormSchemas] = useState<FormSchemaEntry[]>([]);
  const [formSchemasError, setFormSchemasError] = useState<string | null>(null);

  const loadTemplates = useCallback(async () => {
    setTemplatesError(null);
    try {
      const res = await texasApi.templateLibrary.listTemplates();
      const err = apiError(res);
      if (err) { setTemplatesError(err); setTemplates([]); return; }
      const data = unwrap<{ templates?: TemplateEntry[] }>(res);
      setTemplates(data?.templates ?? []);
    } catch (e: any) {
      setTemplatesError(e?.message ?? 'Failed to load template registry');
      setTemplates([]);
    }
  }, []);

  const loadFormSchemas = useCallback(async () => {
    setFormSchemasError(null);
    try {
      const res = await texasApi.templateLibrary.listFormSchemas(jurisdiction || undefined);
      const err = apiError(res);
      if (err) { setFormSchemasError(err); setFormSchemas([]); return; }
      const data = unwrap<{ schemas?: FormSchemaEntry[] }>(res);
      setFormSchemas(data?.schemas ?? []);
    } catch (e: any) {
      setFormSchemasError(e?.message ?? 'Failed to load form schemas');
      setFormSchemas([]);
    }
  }, [jurisdiction]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  useEffect(() => { loadFormSchemas(); }, [loadFormSchemas]);

  const jurisdictions = unique(templates.map((t) => t.jurisdiction));
  const locales = unique(templates.map((t) => t.locale));
  const renderers = unique(templates.map((t) => t.renderer));

  // Facets are derived from the registry response, never a hardcoded template
  // list - F-269's mandate is that the registry owns the catalogue.
  const visible = templates.filter((t) => {
    if (jurisdiction && t.jurisdiction !== jurisdiction) return false;
    if (locale && t.locale !== locale) return false;
    if (renderer && t.renderer !== renderer) return false;
    return true;
  });

  // Distinct template types = slugs with any locale/version variant collapsed.
  const typeCount = unique(visible.map((t) => t.slug)).length;

  const runResolve = async (e: React.FormEvent) => {
    e.preventDefault();
    setResolveError(null);
    setResolved(null);
    setRenderResult(null);
    setRenderError(null);
    try {
      const res = await texasApi.templateLibrary.resolve({
        slug: resolveSlug,
        version: resolveVersion || undefined,
        jurisdiction: resolveJurisdiction || undefined,
        locale: resolveLocale || undefined,
      });
      const err = apiError(res);
      if (err) { setResolveError(err); return; }
      const data = unwrap<TemplateEntry>(res);
      if (!data) { setResolveError('Template resolved to an empty payload'); return; }
      setResolved(data);
    } catch (ex: any) {
      setResolveError(ex?.message ?? 'Failed to resolve template');
    }
  };

  const runRender = async (e: React.FormEvent) => {
    e.preventDefault();
    setRenderError(null);
    setRenderResult(null);
    let payload: Record<string, unknown>;
    try {
      payload = renderData.trim() ? JSON.parse(renderData) : {};
    } catch (ex: any) {
      setRenderError(`Render data is not valid JSON: ${ex?.message ?? 'parse error'}`);
      return;
    }
    const slug = resolved?.slug ?? resolveSlug;
    try {
      const res = await texasApi.templateLibrary.render({
        slug,
        version: (resolved?.version ?? resolveVersion) ? String(resolved?.version ?? resolveVersion) : undefined,
        jurisdiction: resolved?.jurisdiction ?? resolveJurisdiction ?? undefined,
        locale: resolved?.locale ?? resolveLocale ?? undefined,
        data: payload,
      });
      const err = apiError(res);
      if (err) { setRenderError(err); return; }
      const data = unwrap<RenderResult>(res);
      if (!data || !data.base64) { setRenderError('Render returned no PDF payload'); return; }
      setRenderResult(data);
    } catch (ex: any) {
      setRenderError(ex?.message ?? 'Template render failed');
    }
  };

  const pdfSrc = renderResult?.base64
    ? `data:application/pdf;base64,${renderResult.base64}`
    : null;

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="template-library">
      <h1 className="text-2xl font-bold text-gray-900">
        <i className="fas fa-file-contract mr-2" />Template Library
      </h1>

      <section className="bg-white shadow rounded-lg p-6 space-y-4" data-testid="template-registry">
        <h2 className="font-bold text-indigo-600">Template Registry (F-269 / F-270)</h2>

        <div className="flex items-end space-x-2">
          <div>
            <label htmlFor="tpl-jurisdiction" className="block text-sm font-semibold text-gray-900">Jurisdiction</label>
            <input id="tpl-jurisdiction" data-testid="tpl-jurisdiction" className="p-2 bg-white border rounded"
              value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value)} placeholder="TX" />
          </div>
          <div>
            <label htmlFor="tpl-locale" className="block text-sm font-semibold text-gray-900">Locale</label>
            <input id="tpl-locale" data-testid="tpl-locale" className="p-2 bg-white border rounded"
              value={locale} onChange={(e) => setLocale(e.target.value)} placeholder="en / es" />
          </div>
          <div>
            <label htmlFor="tpl-renderer" className="block text-sm font-semibold text-gray-900">Renderer</label>
            <select id="tpl-renderer" data-testid="tpl-renderer" className="p-2 bg-white border rounded"
              value={renderer} onChange={(e) => setRenderer(e.target.value)}>
              <option value="">All renderers</option>
              {renderers.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>

        {templatesError && (
          <div role="alert" data-testid="templates-error" className="p-4 bg-red-50 rounded border border-red-300 text-red-800">
            {templatesError}
          </div>
        )}

        <p className="text-sm text-gray-600" data-testid="template-type-count">
          {typeCount} template type{typeCount === 1 ? '' : 's'} across {visible.length} registered version
          {visible.length === 1 ? '' : 's'}.
          {jurisdictions.length > 0 && <> Jurisdictions in registry: {jurisdictions.join(', ')}.</>}
          {locales.length > 0 && <> Locales: {locales.join(', ')}.</>}
        </p>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-3 text-sm font-semibold">Slug</th>
                <th className="px-4 py-3 text-sm font-semibold">Version</th>
                <th className="px-4 py-3 text-sm font-semibold">Jurisdiction</th>
                <th className="px-4 py-3 text-sm font-semibold">Locale</th>
                <th className="px-4 py-3 text-sm font-semibold">Renderer</th>
                <th className="px-4 py-3 text-sm font-semibold">Title</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {visible.length === 0 && !templatesError && (
                <tr>
                  <td colSpan={6} className="px-4 py-3 text-sm text-gray-500" data-testid="templates-empty">
                    0 registered templates match this filter.
                  </td>
                </tr>
              )}
              {visible.map((t, i) => (
                <tr key={`${t.slug}-${t.version}-${t.locale}-${i}`} data-testid={`template-row-${t.slug}-${t.version}-${t.locale}`}>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">{t.slug}</td>
                  <td className="px-4 py-3 text-sm">{t.version}</td>
                  <td className="px-4 py-3 text-sm">{t.jurisdiction ?? '-'}</td>
                  <td className="px-4 py-3 text-sm">{t.locale ?? '-'}</td>
                  <td className="px-4 py-3 text-sm">{t.renderer ?? '-'}</td>
                  <td className="px-4 py-3 text-sm">{t.title ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="bg-white shadow rounded-lg p-6 space-y-4" data-testid="template-resolve">
        <h2 className="font-bold text-indigo-600">Resolve by (slug, version, jurisdiction, locale)</h2>

        <form className="flex items-end space-x-2 flex-wrap" onSubmit={runResolve} data-testid="resolve-form">
          <div>
            <label htmlFor="resolve-slug" className="block text-sm font-semibold text-gray-900">Slug</label>
            <input id="resolve-slug" data-testid="resolve-slug" className="p-2 bg-white border rounded" required
              value={resolveSlug} onChange={(e) => setResolveSlug(e.target.value)} placeholder="sb38-eviction-notice" />
          </div>
          <div>
            <label htmlFor="resolve-version" className="block text-sm font-semibold text-gray-900">Version</label>
            <input id="resolve-version" data-testid="resolve-version" className="p-2 bg-white border rounded"
              value={resolveVersion} onChange={(e) => setResolveVersion(e.target.value)} placeholder="latest" />
          </div>
          <div>
            <label htmlFor="resolve-jurisdiction" className="block text-sm font-semibold text-gray-900">Jurisdiction</label>
            <input id="resolve-jurisdiction" data-testid="resolve-jurisdiction" className="p-2 bg-white border rounded"
              value={resolveJurisdiction} onChange={(e) => setResolveJurisdiction(e.target.value)} />
          </div>
          <div>
            <label htmlFor="resolve-locale" className="block text-sm font-semibold text-gray-900">Locale</label>
            <input id="resolve-locale" data-testid="resolve-locale" className="p-2 bg-white border rounded"
              value={resolveLocale} onChange={(e) => setResolveLocale(e.target.value)} placeholder="en / es" />
          </div>
          <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded" data-testid="resolve-submit">
            Resolve
          </button>
        </form>

        {resolveError && (
          <div role="alert" data-testid="resolve-error" className="p-4 bg-red-50 rounded border border-red-300 text-red-800">
            {resolveError}
          </div>
        )}

        {resolved && (
          <div className="p-4 bg-gray-50 rounded border" data-testid="resolved-template">
            <div className="text-sm font-semibold text-gray-900" data-testid="resolved-slug">{resolved.slug}</div>
            <div className="text-sm text-gray-600" data-testid="resolved-meta">
              version {resolved.version} &middot; jurisdiction {resolved.jurisdiction ?? '-'} &middot;
              locale {resolved.locale ?? '-'} &middot; renderer {resolved.renderer ?? '-'}
            </div>
            {resolved.title && <div className="text-sm text-gray-600">{resolved.title}</div>}
          </div>
        )}
      </section>

      <section className="bg-white shadow rounded-lg p-6 space-y-4" data-testid="template-render">
        <h2 className="font-bold text-indigo-600">PDF Render (pdf-lib, F-270)</h2>

        <form className="space-y-2" onSubmit={runRender} data-testid="render-form">
          <label htmlFor="render-data" className="block text-sm font-semibold text-gray-900">Field data (JSON)</label>
          <textarea
            id="render-data"
            data-testid="render-data"
            className="p-2 bg-white border rounded w-full"
            rows={4}
            value={renderData}
            onChange={(e) => setRenderData(e.target.value)}
          />
          <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded" data-testid="render-submit">
            Render PDF
          </button>
        </form>

        {renderError && (
          <div role="alert" data-testid="render-error" className="p-4 bg-red-50 rounded border border-red-300 text-red-800">
            <span className="font-semibold">Render error: </span>{renderError}
          </div>
        )}

        {renderResult && (
          <div className="space-y-3" data-testid="render-result">
            <div className="p-4 bg-gray-50 rounded border text-sm text-gray-900">
              <span data-testid="render-page-count">Pages: {renderResult.pageCount}</span>
              {' &middot; '}
              <span data-testid="render-meta">
                {renderResult.template?.slug} v{renderResult.template?.version}
                {' '}({renderResult.template?.jurisdiction}/{renderResult.template?.locale})
                {' via '}{renderResult.template?.renderer}
              </span>
            </div>

            {renderResult.issues && renderResult.issues.length > 0 && (
              <div className="p-4 bg-amber-50 rounded border border-amber-300" data-testid="render-issues">
                <div className="font-semibold text-amber-900">
                  Layout issues ({renderResult.issues.length})
                </div>
                <ul className="list-disc pl-5 text-sm text-amber-900">
                  {renderResult.issues.map((issue, i) => <li key={i} data-testid={`render-issue-${i}`}>{issue}</li>)}
                </ul>
              </div>
            )}

            {renderResult.warnings && renderResult.warnings.length > 0 && (
              <div className="p-4 bg-amber-50 rounded border border-amber-300" data-testid="render-warnings">
                <div className="font-semibold text-amber-900">
                  Warnings ({renderResult.warnings.length})
                </div>
                <ul className="list-disc pl-5 text-sm text-amber-900">
                  {renderResult.warnings.map((w, i) => <li key={i} data-testid={`render-warning-${i}`}>{w}</li>)}
                </ul>
              </div>
            )}

            {renderResult.issues && renderResult.issues.length === 0 && (
              <p className="text-sm text-green-800" data-testid="render-issues-none">
                No layout issues reported for this document.
              </p>
            )}

            {pdfSrc && (
              <div className="space-y-2">
                <iframe
                  title="Rendered template PDF"
                  data-testid="pdf-frame"
                  className="w-full border rounded"
                  style={{ height: 480 }}
                  src={pdfSrc}
                />
                <a
                  href={pdfSrc}
                  download={`${renderResult.template?.slug ?? 'template'}.pdf`}
                  className="text-indigo-600 hover:underline text-sm"
                  data-testid="pdf-download"
                >
                  Download rendered PDF
                </a>
              </div>
            )}
          </div>
        )}
      </section>

      <section className="bg-white shadow rounded-lg p-6 space-y-4" data-testid="form-schemas">
        <h2 className="font-bold text-indigo-600">Form Schemas (F-267) &mdash; supporting panel</h2>

        {formSchemasError && (
          <div role="alert" data-testid="form-schemas-error" className="p-4 bg-red-50 rounded border border-red-300 text-red-800">
            {formSchemasError}
          </div>
        )}

        {formSchemas.length === 0 && !formSchemasError && (
          <p className="text-sm text-gray-500" data-testid="form-schemas-empty">
            0 form schemas registered for this jurisdiction.
          </p>
        )}

        {formSchemas.map((s, i) => (
          <div key={`${s.entityType}-${s.version}-${i}`} className="p-4 bg-gray-50 rounded border" data-testid={`form-schema-${s.entityType}`}>
            <div className="flex justify-between items-center">
              <span className="font-semibold text-gray-900">{s.entityType}</span>
              <span className="text-sm text-gray-600">v{s.version}</span>
            </div>
            <div className="text-sm text-gray-600">
              jurisdictions: {(s.jurisdictions ?? []).join(', ') || '-'}
              {' &middot; '}
              <span data-testid={`form-schema-fieldcount-${s.entityType}`}>
                {(s.fields ?? []).length} field{(s.fields ?? []).length === 1 ? '' : 's'}
              </span>
            </div>
            <ul className="text-sm text-gray-700 list-disc pl-5 mt-1">
              {(s.fields ?? []).map((f, j) => (
                <li key={j} data-testid={`form-schema-field-${s.entityType}-${j}`}>
                  {f.label ?? f.name}{f.type ? ` (${f.type})` : ''}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </section>
    </div>
  );
};

export default TexasTemplateLibrary;